import * as React from 'react';
import {
  FilterOutlined,
  MinusOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Badge, Button, Dropdown, Tooltip } from 'antd';
import {
  ADD_PIPELINE_BUTTON_LABEL,
  MAX_NUM_PLAYGROUNDS,
  PLAYGROUND_CLEAR_CHAT_OPTION_LABEL,
} from '@constants/prompt-explorer';
import { IPipeline } from '@redux/types/types';
import QueryPipelineParams from '@components/search/organisms/queryPipelineParams/queryPipelineParams';
import PromptExplorerPipelineSelect from '../features/PromptExplorerPipelineSelect/PromptExplorerPipelineSelect';
import { PlaygroundMoreOption } from '../PipelinePlayground';
import styles from '../pipelinePlayground.module.scss';

type PipelineData = {
  pipelineName: string;
  playgroundId: string;
};

interface IRenderPipelineActionsBarProps {
  totalPlaygrounds: number;
  pipelineName: string | undefined;
  pipeline: IPipeline | null;
  displayRemovePipelineButton: boolean;
  onSelectPipeline: (data: PipelineData) => void;
  isSearching: boolean;
  playgroundId: string;
  mappedPrompts: Record<string, unknown>;
  onParamsChange: (_: Record<string, unknown>) => void;
  onDisplayFilters: (_: string) => void;
  onRemovePipeline: (_: string) => void;
  onAddNewPipeline: (id: string, name: string) => void;
  handleMoreOptionsDropdownMenuItemClick: (_: { key: string }) => void;
  activeFilters: boolean;
  initialPrompts: string[];
}

export const RenderPipelineActionsBar: React.FC<IRenderPipelineActionsBarProps> = ({
  totalPlaygrounds,
  pipelineName,
  pipeline,
  displayRemovePipelineButton,
  onSelectPipeline,
  isSearching,
  playgroundId,
  onDisplayFilters,
  onParamsChange,
  mappedPrompts,
  onRemovePipeline,
  onAddNewPipeline,
  handleMoreOptionsDropdownMenuItemClick,
  activeFilters,
  initialPrompts,
}) => {
  const reachedMaxPipelines = MAX_NUM_PLAYGROUNDS === totalPlaygrounds;
  const pipelineSelected = !!pipeline;
  const displayAddPipelineButton = !reachedMaxPipelines && pipelineSelected;

  const moreOptionsDropdownMenuItems = [
    {
      key: PlaygroundMoreOption.CLEAR_CHAT,
      label: PLAYGROUND_CLEAR_CHAT_OPTION_LABEL,
      icon: <ReloadOutlined />,
    },
  ];

  return (
    <div className={styles.pipelineActionsBar}>
      <PromptExplorerPipelineSelect
        selectedPipeline={pipeline?.name}
        loading={isSearching}
        onPipelineSelect={(value: string) =>
          onSelectPipeline({ pipelineName: value, playgroundId })
        }
      />
      <div className={styles.pipelineActionsBar_buttonsWrapper}>
        {pipelineSelected && (
          <Badge dot={activeFilters}>
            <Button
              type="text"
              icon={<FilterOutlined />}
              onClick={() => onDisplayFilters(playgroundId)}
            />
          </Badge>
        )}
        {displayRemovePipelineButton && (
          <Button
            type="text"
            icon={<MinusOutlined />}
            onClick={() => onRemovePipeline(playgroundId)}
          />
        )}
        {displayAddPipelineButton && (
          <Tooltip title={ADD_PIPELINE_BUTTON_LABEL} placement="bottom">
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => onAddNewPipeline(playgroundId, pipelineName!)}
            />
          </Tooltip>
        )}
        {pipelineSelected && (
          <Dropdown
            menu={{
              items: moreOptionsDropdownMenuItems,
              onClick: handleMoreOptionsDropdownMenuItemClick,
            }}
            trigger={['click']}
          >
            <Button type="text" onClick={(e) => e.preventDefault()} icon={<MoreOutlined />} />
          </Dropdown>
        )}
        {pipelineSelected && (
          <QueryPipelineParams
            initialPrompts={initialPrompts}
            onParamsChange={onParamsChange}
            params={mappedPrompts}
          />
        )}
      </div>
    </div>
  );
};
